import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http'; // Import HttpClient and related
import { catchError, tap } from 'rxjs/operators';
import { AuthContextService } from './services/auth-context.service';
import { environment } from '../environments/environment';

export interface ChatMessage {
  type: 'user' | 'model' | 'gpt';
  content: string;
  date: string;
  file?: any[];
  references?: any[];
  imageUrl?: string;  // For DALL-E generated images
}

export interface ChatSession {
  id: string;
  title: string;
  timestamp: string;
  history: ChatMessage[];
  name: string;
  temperature?: number;
  model?: string;
  userId?: string;
  pinned?: boolean;
}
export interface ChatSessions {
  id: string;
  title: string;
  timestamp: string;
  history: ChatMessage[];
  name?: string;
  temperature?: number;
  model?: string;
  userId?: string;
  pinned?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ChatSessionService {
  private maxStoredSessions = 50; // Limit stored sessions to prevent quota issues
  private pinnedSessionIds = new Set<string>();
  private currentSessionSubject: BehaviorSubject<ChatSession | null> =
    new BehaviorSubject<ChatSession | null>(null);
  public currentSession$: Observable<ChatSession | null> =
    this.currentSessionSubject.asObservable();
  private allSessionsSubject: BehaviorSubject<ChatSession[]> =
    new BehaviorSubject<ChatSession[]>([]);
  public allSessions$: Observable<ChatSession[]> =
    this.allSessionsSubject.asObservable();
  // Tracks whether a user's sessions are currently being fetched from the server,
  // so the UI can show a loading state instead of an "empty" message during retrieval.
  private sessionsLoadingSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public sessionsLoading$: Observable<boolean> =
    this.sessionsLoadingSubject.asObservable();
  private apiUrl = environment.apiBaseUrl; // Base API URL
  private isNewChatBlocked: boolean = false; // Block session changes during new chat

  constructor(private http: HttpClient, private authContext: AuthContextService) {
    this.loadAllSessions();
    // Don't automatically create or set any session - wait for user interaction
    // The reference component will handle session restoration based on user intent
  }

  private getPinnedSessionIds(): Set<string> {
    return new Set(this.pinnedSessionIds);
  }

  private savePinnedSessionIds(pinnedIds: Set<string>): void {
    this.pinnedSessionIds = new Set(pinnedIds);
  }

  private applyPinnedState(sessions: ChatSession[]): ChatSession[] {
    const pinnedIds = this.getPinnedSessionIds();
    return sessions.map(session => ({
      ...session,
      pinned: pinnedIds.has(session.id)
    }));
  }

  private updatePinnedState(sessionId: string, isPinned: boolean): void {
    const pinnedIds = this.getPinnedSessionIds();
    if (isPinned) {
      pinnedIds.add(sessionId);
    } else {
      pinnedIds.delete(sessionId);
    }
    this.savePinnedSessionIds(pinnedIds);
  }

  private sanitizeTitle(value: string | undefined | null): string {
    return (value || '').replace(/^["'“”]+|["'“”]+$/g, '');
  }

  private normalizeSession(session: ChatSession): ChatSession {
    const preferred = (session.name && session.name.trim().length > 0)
      ? session.name
      : (session.title && session.title.trim().length > 0)
      ? session.title
      : 'New Chat';
    const sanitized = this.sanitizeTitle(preferred);
    return { ...session, name: sanitized, title: sanitized };
  }

  private loadAllSessions(): void {
    this.allSessionsSubject.next([]);
  }

  private saveAllSessions(sessions: ChatSession[]): void {
    try {
      const normalized = sessions.map((s) => this.normalizeSession(s));
      
      const sortedSessions = [...normalized].sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      
      const sessionsToStore = sortedSessions.slice(0, this.maxStoredSessions);

      this.allSessionsSubject.next(sessionsToStore);
    } catch (error) {
      this.allSessionsSubject.next([]);
    }
  }

  // Public method to set all sessions from external source (like API)
  public setAllSessions(sessions: ChatSession[]): void {
    const normalized = sessions.map((s) => this.normalizeSession(s));
    // Apply pinned state from memory when setting sessions from API
    const withPinnedState = this.applyPinnedState(normalized);
    this.saveAllSessions(withPinnedState);
  }

  // Check if there's already an empty "New Chat" session
private hasEmptyNewChatSession(): ChatSession | null {
  const sessions = this.allSessionsSubject.value;
  return (
    sessions.find(
      (session) =>
        (session.title === 'New Chat' || session.name === 'New Chat') &&
        session.history.length === 0
    ) || null
  );
}


public createNewSession(
  model: string = 'gpt4-o',
  temperature: number = 0.7
): void {
  // ✅ Check for an existing empty "New Chat" session
  const existingEmptyNewChat = this.hasEmptyNewChatSession();

  if (existingEmptyNewChat) {    this.setCurrentSession(existingEmptyNewChat);
    return;
  }

  // ✅ No empty "New Chat" exists — create a new one with selected model
  const newSession: ChatSession = {
    id: this.generateUniqueId(),
    title: 'New Chat',
    name: 'New Chat',
    timestamp: new Date().toISOString(),
    history: [],
    model,
    temperature,
    userId: this.authContext.getUserId(),
  };

  const sessions = this.allSessionsSubject.value;
  sessions.unshift(newSession); // add new session to top
  this.saveAllSessions(sessions);
  this.setCurrentSession(newSession);}

// Create a session only when user starts interacting (sends first message)
public createSessionOnFirstInteraction(
  model: string = 'gpt4-o',
  temperature: number = 0.7
): ChatSession {
  const newSession: ChatSession = {
    id: this.generateUniqueId(),
    title: 'New Chat',
    name: 'New Chat',
    timestamp: new Date().toISOString(),
    history: [],
    model,
    temperature,
    userId: this.authContext.getUserId(),
  };

  const sessions = this.allSessionsSubject.value;
  sessions.unshift(newSession); // add new session to top
  this.saveAllSessions(sessions);
  this.setCurrentSession(newSession);  return newSession;
}

// Clean up empty sessions that were never used
public cleanupEmptySessions(): void {
  const sessions = this.allSessionsSubject.value;
  const sessionsToKeep = sessions.filter(session => {
    // Keep sessions that have actual conversation history
    const hasUserMessage = session.history?.some(msg => msg.type === 'user');
    const hasModelResponse = session.history?.some(msg => msg.type === 'model' || msg.type === 'gpt');
    return hasUserMessage && hasModelResponse;
  });
  
  if (sessionsToKeep.length !== sessions.length) {    this.saveAllSessions(sessionsToKeep);
  }
}





  // Set the current active session
 public setCurrentSession(session: ChatSession | null): void {
  // Check if we should block this session change
  if (this.isNewChatBlocked && session !== null) {    return; // Don't set the session
  }  this.currentSessionSubject.next(session ? this.normalizeSession(session) : null);
}


  // Get the current active session
  public getCurrentSession(): ChatSession | null {
    return this.currentSessionSubject.value;
  }

  // Add a message to the current session's history and update storage
  public addMessageToCurrentSession(message: ChatMessage): void {
    const currentSession = this.currentSessionSubject.value;
    if (currentSession) {
      currentSession.history.push(message);
      // Update the title if it's still 'New Chat' and the first user message comes in
      if (
        currentSession.title === 'New Chat' &&
        message.type === 'user' &&
        currentSession.history.length === 1
      ) {
        const generated = this.generateChatTitle(message.content);
        currentSession.title = generated;
        currentSession.name = generated; // keep name in sync for display
      }
      currentSession.timestamp = new Date().toISOString(); // Update timestamp on new message
      
      // Only save to API after we have both user question and model response
      const hasUserMessage = currentSession.history.some(msg => msg.type === 'user');
      const hasModelResponse = currentSession.history.some(msg => msg.type === 'model' || msg.type === 'gpt');
      
      if (hasUserMessage && hasModelResponse) {
        this.updateSession(currentSession); // This will also save to API via updateSession
      } else {
        // Just update local storage for now
        this.saveAllSessions(this.allSessionsSubject.value);
      }
    }
  }

  // Update an existing session in storage AND API
  public updateSession(updatedSession: ChatSession): void {
    let sessions = this.allSessionsSubject.value;
    const index = sessions.findIndex((s) => s.id === updatedSession.id);
    if (index > -1) {
      const normalizedSession = this.normalizeSession(updatedSession);
      // Preserve the pinned state from the updated session
      normalizedSession.pinned = updatedSession.pinned;
      sessions[index] = normalizedSession;
      this.saveAllSessions(sessions);

      this.updatePinnedState(updatedSession.id, !!updatedSession.pinned);

      // If the updated session is the current one, update the subject as well
      if (this.currentSessionSubject.value?.id === updatedSession.id) {
        this.currentSessionSubject.next(normalizedSession);
      }
      this.saveSessionToApi(updatedSession).subscribe(); // Save updated session to API
    }
  }

  // Delete a session from storage AND API
  public deleteSession(sessionId: string): void {
    const userId = this.authContext.getUserId();
    if (!userId) {      return;
    }

    this.deleteSessionFromApi(sessionId, userId).subscribe({
      next: () => {        let sessions = this.allSessionsSubject.value.filter(
          (s) => s.id !== sessionId
        );
        this.saveAllSessions(sessions);
        // If the deleted session was the current one, clear current session
        if (this.currentSessionSubject.value?.id === sessionId) {
          this.currentSessionSubject.next(null);
          // If no sessions left, create a new one
          if (sessions.length === 0) {
            this.createNewSession();
          } else {
            // Set the first remaining session as current
            this.setCurrentSession(sessions[0]);
          }
        } else if (sessions.length > 0) {
          // If the deleted session was not current, but there are other sessions, select the first one
          this.setCurrentSession(sessions[0]);
        } else {
          // If no sessions left, create a new one
          this.createNewSession();
        }
      },
      error: (err) => {        // Optionally, alert the user or show a snackbar
      },
    });
  }

  // Generate a unique ID for sessions
  private generateUniqueId(): string {
    return 'session_' + Date.now() + Math.random().toString(36).substr(2, 9);
  }

  private generateChatTitle(content: string): string {
    // Remove surrounding quotes and common question words, then extract key topics
    const noQuotes = (content || "").replace(/^["'“”]+|["'“”]+$/g, "");
    const cleanContent = noQuotes.toLowerCase()
      .replace(/^(what|how|where|when|why|who|which|can|could|would|should|is|are|do|does|did|will|tell me|explain|describe|list|show me|help me|give me)\s+/i, '')
      .replace(/\?+$/, '')
      .trim();

    // Extract key phrases and topics
    const keyWords = cleanContent.split(/\s+/)
      .filter(word => word.length > 3 && !['about', 'with', 'from', 'that', 'this', 'they', 'them', 'their', 'there', 'then', 'than', 'some', 'more', 'most', 'many', 'much', 'very', 'just', 'only', 'also', 'even', 'still', 'well', 'good', 'best', 'better'].includes(word))
      .slice(0, 3);

    if (keyWords.length === 0) {
      return noQuotes.substring(0, 30) + (noQuotes.length > 30 ? '...' : '');
    }

    // Create a title by capitalizing key words
    const title = keyWords
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    // Handle specific patterns for better titles
    if (content.toLowerCase().includes('publisher') || content.toLowerCase().includes('publishing')) {
      return title.includes('Publishing') ? title : title + ' Publishing';
    }
    if (content.toLowerCase().includes('book') || content.toLowerCase().includes('books')) {
      return title.includes('Book') ? title : title + ' Books';
    }
    if (content.toLowerCase().includes('author') || content.toLowerCase().includes('authors')) {
      return title.includes('Author') ? title : title + ' Authors';
    }

    return title.length > 25 ? title.substring(0, 25) + '...' : title;
  }

  // Clear all sessions (local and optionally from API)
  public clearAllSessions(): void {
    const sessions = this.allSessionsSubject.value;
    const userId = this.authContext.getUserId();
    this.allSessionsSubject.next([]);
    this.currentSessionSubject.next(null);
    // Automatically create a new session when clearing all sessions
    this.createNewSession();  }

  // --- API Integration Methods ---

  // Fetches all sessions for a user from the API
  public fetchAllSessionsFromApi(userId: string): Observable<ChatSession[]> {
    const apiUrl = `${this.apiUrl}/sessions/user/${userId}?db_name=v2`;
    return this.http.get<ChatSession[]>(apiUrl).pipe(
      tap((sessions) => {        // Update in-memory session list with fetched data
        // Apply pinned state from memory
        const normalized = sessions.map((s) => this.normalizeSession(s));
        const withPinnedState = this.applyPinnedState(normalized);
        this.saveAllSessions(withPinnedState);      }),
      catchError(this.handleError<ChatSession[]>('fetchAllSessionsFromApi', []))
    );
  }

  // Fetches a specific session by ID from the API
  public fetchSessionByIdFromApi(
    sessionId: string,
    userId: string
  ): Observable<ChatSession> {
    const apiUrl = `${this.apiUrl}/sessions/view/${sessionId}?db_name=v2`;
    return this.http.get<ChatSession>(apiUrl).pipe(
      tap((session) => {        // Ensure the fetched session is integrated into the allSessions list
        const sessions = this.allSessionsSubject.value;
        const normalized = this.normalizeSession(session);
        // Preserve pinned state from memory
        const pinnedIds = this.getPinnedSessionIds();
        normalized.pinned = pinnedIds.has(normalized.id);
        const index = sessions.findIndex((s) => s.id === normalized.id);
        if (index > -1) {
          sessions[index] = normalized; // Update existing
        } else {
          sessions.unshift(normalized); // Add if new
        }
        this.saveAllSessions(sessions); // Persist updated list
        this.setCurrentSession(normalized); // Set as current session
      }),
      catchError(this.handleError<ChatSession>('fetchSessionByIdFromApi'))
    );
  }

  // Saves (creates/updates) a session to the API
  public saveSessionToApi(session: ChatSession): Observable<any> {
    const userId = this.authContext.getUserId();
    
    // Strip large base64 file data from history before saving to backend
    const sanitizedHistory = session.history.map((msg: ChatMessage) => {
      const sanitizedMsg = { ...msg };
      if (sanitizedMsg.file && sanitizedMsg.file.length > 0) {
        // Keep only file metadata, remove base64 content
        sanitizedMsg.file = sanitizedMsg.file.map((f: any) => ({
          name: f.name,
          type: f.type,
          size: f.size,
          // Exclude base64, preview, and img to reduce payload size
        }));
      }
      return sanitizedMsg;
    });
    
    const payload = {
      id: session.id,
      name: session.name || session.title, // Prefer edited name; fallback to title
      temperature: session.temperature,
      model: session.model,
      timestamp: session.timestamp,
      history: sanitizedHistory, // Use sanitized history without large file data
      userId: userId,
      db_name: "v2"
    };
    const headers = { 'Content-Type': 'application/json' };
    return this.http.post(`${this.apiUrl}/sessions`, payload, { headers }).pipe(
      catchError(this.handleError<any>('saveSessionToApi'))
    );
  }

  // Deletes a session from the API
  public deleteSessionFromApi(
    sessionId: string,
    userId: string
  ): Observable<any> {
    const apiUrl = `${this.apiUrl}/sessions/delete`;
    const payload = { id: sessionId, userId: userId, db_name: "v2" };
    return this.http.post(apiUrl, payload).pipe(
      catchError(this.handleError<any>('deleteSessionFromApi'))
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {      // Let the app continue by returning an empty result or rethrowing for UI
      return throwError(
        () => new Error(`Something went wrong`)
      );
    };
  }

  public fetchUserSessionsAndSet(userIds?: string): void {    const userId = userIds || this.authContext.getUserId();
    const apiUrl = `${this.apiUrl}/sessions/user/${userId}?db_name=v2`;
    this.sessionsLoadingSubject.next(true);
    this.http.get<ChatSession[]>(apiUrl).subscribe({
      next: (sessions) => {        // Apply pinned state from memory
        const normalized = sessions.map((s) => this.normalizeSession(s));
        const withPinnedState = this.applyPinnedState(normalized);
        this.saveAllSessions(withPinnedState);
        // Clean up any empty sessions that might exist
        this.cleanupEmptySessions();

        const remainingSessions = this.allSessionsSubject.value;
        this.sessionsLoadingSubject.next(false);
      },
      error: (error) => {        this.sessionsLoadingSubject.next(false);
      },
    });
  }

  // Methods to control new chat blocking
  public blockNewChatSessionChanges(): void {    this.isNewChatBlocked = true;
  }

  public unblockNewChatSessionChanges(): void {    this.isNewChatBlocked = false;
  }
}
